import angular from "angular";
import { ApiImportStatus, Dates, Numeric, EnumerationsLogic, Configs, ApiValidation, DataMapper, ApiValidationErrorType, OpenAPIV3, Occupations, Translations } from "@salaxy/core";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { JsonSchemaService } from "../../../services";


let XLSX = null;

/**
 * Controller for data imports.
 */
export abstract class DataImportControllerBase<TSource, TTarget, TOptions> implements angular.IController {

  /** Tabular source row data. */
  public sourceData: Array<TSource & { validation?: ApiValidation, importStatus?: ApiImportStatus }>;

  /** Target objects mapped from source data, and which should be imported */
  public targetData:  Array<TTarget & { validation?: ApiValidation, result?: {status?: ApiImportStatus}}>;

  /** Table control*/
  public table: any;

  /** Current action */
  public action: "upload" | "validate" | "import" = "upload";

  /** Current import count */
  public importCurrent = 0;

  /** Total import count */
  public importTotal = 0;

  /** Flag for import in progress. */
  public isImporting = false;

  /** Flag for cancel. */
  public isCanceled = false;

  /** Optional options */
  public options: TOptions;

  /** Template for options */
  public optionsTemplate: string;

  /** Data mapper */
  protected mapper: DataMapper<TSource, TTarget>;

  /**
   * Creates a new DataImportController
   * @ignore
   */
  constructor(
    /** JsonSchemaService */
    protected jsonSchemaService: JsonSchemaService,
    /** Angular timeout service */
    protected $timeout: angular.ITimeoutService,
  ) {
  }

  /**
   * Implement IController
   */
  public async $onInit(){
    // Support both esm and umd builds...
    XLSX = await Promise.resolve(Configs.global.XLSX);
    this.init();
  }

  /**
   * Download data or template
   * @param helpData Help data in the form or array of arrays
   */
  public async download(helpData = null) {
    // Take copy
    const copied = angular.copy(this.sourceData);
    if (copied.length < 1) {
      const emptyRow: any = { };
      this.mapper.source.keys.forEach((key) => {
        emptyRow[key] = null;
      });
      copied.push(emptyRow);
    }
     // remove validation
     for(const row of copied) {
      delete row.validation;
    }
    const sheet = XLSX.utils.json_to_sheet(copied);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "data");
    if (helpData){
      const helpSheet = XLSX.utils.aoa_to_sheet(helpData);
      XLSX.utils.book_append_sheet(workbook, helpSheet, "help");
    }
    XLSX.writeFile(workbook, "import.xlsx");
  }

  /** Upload rows to table */
  public async upload(files: any[]) {
    if (files && files.length) {
      this.reset();
      const file = files[0];
      const fileData = await file.arrayBuffer();
      /* data is an ArrayBuffer */
      const workbook = XLSX.read(fileData);
      if (workbook?.SheetNames?.length > 0) {
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const sheetData = XLSX.utils.sheet_to_json(sheet);
        this.sourceData = [];
        sheetData.forEach( (sheetRow) => {
          if (this.hasData(sheetRow)) {
            const row: any = { validation: {}};
            this.mapper.source.keys.forEach((key) => {
              this.setFieldValue(row,key,sheetRow[key]);
            });
            this.sourceData.push(row);
          }
        });
      }
      this.table.setData(this.sourceData);
      await this.$timeout();
    }
  }

  /** Reset data */
  public reset(setData = false) {
    this.importTotal = 0;
    this.importCurrent = 0;
    this.isImporting = false;
    this.isCanceled = false;
    this.sourceData = [];
    this.targetData = null;
    if (this.table && setData) {
      this.table.setData(this.sourceData);
      this.$timeout();
    }
  }

  /** Paste data */
  public async paste() {
    const text = await (navigator as any).clipboard.readText();
    if (text) {
			const rowData =  this.table.modules.clipboard.pasteParser(text);
			if(rowData){
				this.table.modules.clipboard.pasteAction(rowData);
      }
      await this.$timeout();
    }
  }

  /** Map and validate data */
  public async validate() {
    this.importTotal = 0;
    let isValid = true;
    for(const row of this.sourceData) {
      let isValidRow = true;
      row.importStatus = ApiImportStatus.Ignored;
      row.validation = null;
      for(const field of this.mapper.source.keys) {
        if (!this.validateField(field, row[field])) {
          if (isValidRow) {
            isValidRow = false;
            isValid = false;
            row.validation = {
              isValid: false,
              errors: [
               {
                 type: ApiValidationErrorType.General,
                 msg: `${Translations.get("SALAXY.NG1.DataImport.messageInvalid")} ${this.getFieldTitle(field)}`
               }
              ]
            };
          }
        }
      }
      if (isValidRow) {
        row.validation = {
          isValid : true,
          errors: [],
        };
      }
    }
    if (isValid) {
      this.targetData = this.mapper.doMap(this.sourceData);
    }
    else
    {
      this.targetData = null;
    }
    this.table.setData(this.sourceData);
    await this.$timeout();
  }

  /** Returns extra validator */
  public getExtraValidators(): Array<{ validate: () => void, buttonLabel: string }> {
    return [];
  }

  /** Enrichs data */
  public getDataEnrichers(): Array<{ enrich: () => void, buttonLabel: string }> {
    return [];
  }

  /** Cancel import */
  public cancel() {
    this.isCanceled = true;
  }

  /**
   * Options validation. If required, override this method in the inheritied class.
   */
  public hasInvalidOptions() {
    return false;
  }

  /** Import data */
  public abstract import();

  /** Help */
  public abstract help();

  /** Returns number of undefined validation rows */
  public get validationUndoneRows() {
    return this.sourceData.filter( (r) => !r.validation?.isValid &&  r.validation?.isValid !== false).length;
  }

  /** Returns number of success rows */
  public get validationSuccessRows() {
    return this.sourceData.filter( (r) => r.validation?.isValid === true).length;
  }

  /** Returns number of error rows */
  public get validationErrorRows() {
    return this.sourceData.filter( (r) => r.validation?.isValid === false).length;
  }

  /** Returns number of success rows */
  public get importSuccessRows() {
    return this.getImportStatusRows(ApiImportStatus.Success);
  }

  /** Returns number of error rows */
  public get importErrorRows() {
    return this.getImportStatusRows(ApiImportStatus.Error);
  }

  /** Returns number of warning rows */
  public get importWarningRows() {
    return this.getImportStatusRows(ApiImportStatus.Warning);
  }

  /** Returns number of success rows */
  public get importIgnoredRows() {
    return this.getImportStatusRows(ApiImportStatus.Ignored);
  }

  protected getFieldTitle(field: string): string {
    return field;
  }

  protected getEditor(field: string): any {
    // OpenAPIV3.NonArraySchemaObjectType "string" | "number" | "boolean" | "object" | "integer"
    const fieldSchema = this.mapper.source.schema.properties[field] as OpenAPIV3.SchemaObject;


    switch (fieldSchema?.type) {
      case "string":
      {
        switch ( fieldSchema.format ) {
          case "date":
            return { editor: "date" };
          case "date-time":
            return { editor: "datetime" };
          case "multiline":
            return { editor: "textarea" };
        }
        if (fieldSchema.format == "Occupation") {
          const occupations = Occupations.getAll();
          return {
            editor: "list",
            editorParams: {
             values: occupations.map( (e) =>  ({value: e.code, label: e.label})),
             autocomplete: true,
            }
          };
        } else if (fieldSchema.format && fieldSchema.enum?.length > 0) {
          const enumMetadata = EnumerationsLogic.getEnumMetadata(fieldSchema.format);
          return {
            editor: "list",
            formatter:function(cell, /*formatterParams, onRendered */){
              //cell - the cell component
              //formatterParams - parameters set for the column
              //onRendered - function to call when the formatter has been rendered
              const value = cell.getValue();
              if (!value) {
                return null;
              }
              return enumMetadata.values.find( (e) => e.name == value)?.label ?? value;
            },
            editorParams: {
             values: enumMetadata.values.map( (e) =>  ({value: e.name, label: e.label})),
            }
          };
        }
        break;
      }
      case "number":
      case "integer":
          return { editor: "number" };
      case "boolean":
          return { editor: "tickCross" };
    }
    return { editor: "input" };
  }

  protected validateField(field: string, value: any): boolean {
    if (!field) {
      return true;
    }
    const fieldSchema = this.mapper.source.schema.properties[field] as OpenAPIV3.SchemaObject;
    switch (fieldSchema?.type) {
      case "string":{
          if (!value) {
            return true;
          }
          if ( typeof value !== "string" ) {
            return false;
          }
          switch ( fieldSchema.format ) {
            case "date":
            case "date-time":
            {
              return Dates.isValidDateTime(value);
            }
          }
          if (fieldSchema.format == "Occupation") {
            const occupations = Occupations.getAll();
            return occupations.some( (o) => o.code === value);
          } else if (fieldSchema.format && fieldSchema.enum?.length > 0) {
            const enumMetadata = EnumerationsLogic.getEnumMetadata(fieldSchema.format);
            return enumMetadata.values.some( (e) => e.name === value);
          }
          return true;
        }
      case "number":
      case "integer": {
          if (value == null || value == undefined) {
            return true;
          }
          return typeof value === "number" || typeof value === "bigint";
        }
      case "boolean": {
          if (value == null || value == undefined) {
            return true;
          }
          return typeof value === "boolean";
        }
      default: {
          if (value == null || value == undefined) {
            return true;
          }
          return typeof value === "string";
        }
      }
  }

  /** Default help data for columns. */
  protected getDefaultHelpDataForColumns() {
    return [
      [Translations.get("SALAXY.NG1.DataImport.help.fields.title")],
      [Translations.get("SALAXY.NG1.DataImport.help.fields.id"), Translations.get("SALAXY.NG1.DataImport.help.fields.type"), Translations.get("SALAXY.NG1.DataImport.help.fields.name"), Translations.get("SALAXY.NG1.DataImport.help.fields.description")],
      ...(this.mapper.source.keys.map((f) =>
      [
        f,
        (this.mapper.source.schema.properties[f] as any)?.format ||  (this.mapper.source.schema.properties[f] as any)?.type || "string",
        Translations.getWithDefault((this.mapper.source.schema.properties[f] ?? {})["x-salaxy-title"],""),
        Translations.getWithDefault((this.mapper.source.schema.properties[f] ?? {})["x-salaxy-description"],"")
      ])),
      [""],
      [Translations.get("SALAXY.NG1.DataImport.help.values.title")],
      [Translations.get("SALAXY.NG1.DataImport.help.values.type"),Translations.get("SALAXY.NG1.DataImport.help.values.value"), Translations.get("SALAXY.NG1.DataImport.help.values.description")],
      ...(this.mapper.source.keys.filter((f) => this.getEditor(f)?.editor === "list").flatMap((f) =>
        this.getEditor(f)?.editorParams.values.map( (v) => [(this.mapper.source.schema.properties[f] as any).format, v.value, v.label] as any)
      ))
    ];
  }

  private setFieldValue(row: any, field: string, value: any) {
    const fieldSchema = this.mapper?.source.schema.properties ? this.mapper.source.schema.properties[field] as OpenAPIV3.SchemaObject : undefined;
    switch (fieldSchema?.type) {
      case "string": {
        if ( value != null && value != undefined) {
          if (fieldSchema.format == "date" || fieldSchema.format == "date-time") {
            if (typeof value === "number") {
              const y1900 = Dates.getDate(1900,1,1);
              value = Dates.add(y1900, value - 2, "day");
            }
          }
          else if (fieldSchema.format && (fieldSchema.enum?.length ?? -1) > 0) {
            const enumMetadata = EnumerationsLogic.getEnumMetadata(fieldSchema.format);
            const lowerValue = value.toLowerCase();
            const candidate = enumMetadata.values.find( (e) => e.name.toLowerCase() === lowerValue);
            if (candidate) {
              value = candidate.name;
            }
          }
          else {
            if (typeof value === "number" || typeof value === "bigint" || typeof value === "boolean" ) {
              value = `${value}`;
            }
          }
        }
        break;
      }
      case "number": {
        if ( value != null && value != undefined && typeof value === "string") {
          value = Numeric.parseNumber(value);
        }
        break;
      }
      case "integer": {
        if ( value != null && value != undefined && typeof value === "string") {
          value = Numeric.round(Numeric.parseNumber(value),0);
        }
        break;
      }
      case "boolean": {
        if ( value != null && value != undefined && typeof value === "string") {
          value = ( value.toLowerCase() == "TRUE" );
        }
        break;
      }
      default:
        break;
    }
    row[field] = value;
  }

  /** Returns the import mapper */
  protected abstract getImportMapper(): Promise<DataMapper<TSource, TTarget>>;


  private getImportStatusRows(status: ApiImportStatus) {
    return this.sourceData.filter( (r) => r.importStatus == status).length;
  }

  private async init() {

    this.mapper = await this.getImportMapper();
    this.reset();
    this.table = new Tabulator("#import-table", {
      height: "calc(100vh - 500px)", // set height of table (in CSS or here), this enables the Virtual DOM and improves render speed dramatically (can be any valid css height value)
      data: this.sourceData, //assign data to table
      layout: "fitColumns", //fit columns to width of table (optional)
      clipboard: true,
      clipboardPasteAction: ((sheetData: any) => {
        this.reset();
        sheetData.forEach( (sheetRow) => {
          if (this.hasData(sheetRow)) {
            const row = { validation: {}} as any;
            this.mapper.source.keys.forEach((key) => {
              this.setFieldValue(row,key,sheetRow[key]);
            });
            this.sourceData.push(row);
          }
        });
        this.table.setData(this.sourceData);
        return this.sourceData;
      }) as any,
      columns: [
        {
          title: Translations.get("SALAXY.NG1.DataImport.columnImportStatus"),
          field: "importStatus",
          formatter:function(cell, /*formatterParams, onRendered */){
            //cell - the cell component
            //formatterParams - parameters set for the column
            //onRendered - function to call when the formatter has been rendered

            const status = cell.getValue() ?? ApiImportStatus.Ignored;
            switch (status) {
              case ApiImportStatus.Ignored:
                return `<span class="text-default">${Translations.get("SALAXY.NG1.DataImport.ignoredLabel")}</span>`;
              case ApiImportStatus.Success:
                return `<span class="text-success" >${Translations.get("SALAXY.NG1.DataImport.successLabel")}</span>`;
              case ApiImportStatus.Error:
                return `<span class="text-danger" >${Translations.get("SALAXY.NG1.DataImport.errorLabel")}</span>`;
              case ApiImportStatus.Warning:
                return `<span class="text-warning" >${Translations.get("SALAXY.NG1.DataImport.warningLabel")}</span>`;
            }
            return null;
          },
          headerSort:false,
        },
        {
          title: Translations.get("SALAXY.NG1.DataImport.columnValidation"),
          field: "validation",
          formatter:function(cell, /*formatterParams, onRendered */){
            //cell - the cell component
            //formatterParams - parameters set for the column
            //onRendered - function to call when the formatter has been rendered

            const validation: ApiValidation = cell.getValue();
            if (!validation) {
              return null;
            }

            // null or undefined
            if (!validation.isValid && validation.isValid !== false ) {
              if (validation.errors?.length > 0) {
                return `<span class="text-warning">${(validation.errors ?? []).map( (e) => e.msg).join("<br>")}</span>`;
              }
              return null;
            }

            if (validation.isValid !== false) {
              if (validation.errors?.length > 0) {
                return `<span class="text-success">${(validation.errors ?? []).map( (e) => e.msg).join("<br>")}</span>`;
              }
              return `<span class="text-success">Ok</span>`;
            }
            else {
              return `<span class="text-danger" >${(validation.errors ?? []).map( (e) => e.msg).join("<br>")}</span>`;
            }
          },
          headerSort:false,
        },
        ... this.mapper.source.keys.map((x) => ({
          title: this.getFieldTitle(x),
          field: x,
          ... this.getEditor(x),
          headerSort:false,
          headerVertical:"flip",
        }))
      ]
    });

    //trigger an alert message when the row is clicked
    this.table.on("cellEdited", async () => {
      await this.$timeout();
    });

    await this.$timeout();
  }

  private hasData(sheetRow: any): boolean {
    if (!sheetRow) {
      return false;
    }
    let dataOk = false;
    for(const key of Object.keys(sheetRow)) {
      dataOk = dataOk || (sheetRow[key] != null && sheetRow[key] != undefined &&
          !(typeof sheetRow[key] === "string" && sheetRow[key].trim().length == 0));
      if (dataOk) {
        return dataOk;
      }
    }
    return dataOk;
  }
}